import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import Login from './pages/Login';
import Register from './pages/Register';
import CheckEmail from './pages/CheckEmail';
import ConfirmEmail from './pages/ConfirmEmail';
import StudentDashboard from './pages/StudentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import AdminDashboard from './pages/AdminDashboard';
import LessonPage from './pages/LessonPage';
import SubmissionView from './pages/SubmissionView';
import InviteEntry from './pages/InviteEntry';
import FrontPage from './pages/FrontPage';
import NotFound from './pages/NotFound';
import { LoadingState } from './components/ui';
import CheckoutReturn from './components/CheckoutReturn';

const ProtectedRoute: React.FC<{ children: React.ReactElement; role?: string }> = ({ children, role }) => {
  const { user, loading, connectionError, retry } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingState className="min-h-screen" />;
  }

  if (!user && connectionError) {
    // The session may well be valid: don't throw the user out because the server didn't answer.
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4 text-center text-gray-700 dark:text-gray-300">
        <p>Can't reach the server. Check your connection and try again.</p>
        <button onClick={retry} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!user) {
    // Come back here after logging in; /dashboard is where login goes anyway.
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={next === '/dashboard' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const DashboardRouter: React.FC = () => {
  const { user } = useAuth();

  if (user?.role === 'teacher') {
    return <TeacherDashboard />;
  } else if (user?.role === 'student') {
    return <StudentDashboard />;
  } else if (user?.role === 'admin') {
    return <AdminDashboard />;
  }

  return <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              {/* Back from the website's checkout page (PLAN-17), on whichever page the user bought from. */}
              <CheckoutReturn />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/signup" element={<Register />} />
                {/* Email confirmation (ACC-16); before /:inviteCode, like every top-level page. */}
                <Route path="/check-email" element={<CheckEmail />} />
                <Route path="/confirm-email" element={<ConfirmEmail />} />
                <Route path="/submission/:shareToken" element={<SubmissionView />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardRouter />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lesson/:lessonId"
                  element={
                    <ProtectedRoute>
                      <LessonPage />
                    </ProtectedRoute>
                  }
                />
                {/* Exact `/`: the public front page for visitors (WEB-12); logged-in users go on to /dashboard. */}
                <Route path="/" element={<FrontPage />} />
                {/* Any other single segment is an invite link; InviteEntry shows NotFound when it can't be a code. */}
                <Route path="/:inviteCode" element={<InviteEntry />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Router>
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
